import React, { useMemo, useState } from 'react';
import { Button, Collapse, Layout, Modal, Tabs, Typography } from 'antd';
import {
  ArrowRightOutlined,
  CheckCircleFilled,
  CustomerServiceOutlined,
  DesktopOutlined,
  DownloadOutlined,
  GlobalOutlined,
  LaptopOutlined,
  LockOutlined,
  MessageOutlined,
  MobileOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
  SoundOutlined,
  WechatOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import PublicHeader from '../components/layout/PublicHeader';
import PublicFooter from '../components/layout/PublicFooter';
import './GuidePage.css';

const { Content } = Layout;
const { Text, Title, Paragraph } = Typography;

const guideSections = {
  desktop: {
    eyebrow: '教室接收端',
    title: '桌面客户端',
    description: '适合教室电脑，用班级口令连接后即可在后台稳定接收广播、通知和文件。',
    icon: <DesktopOutlined />,
    color: '#2563eb',
    steps: [
      {
        title: '安装或打开客户端',
        text: '首次使用可从下载中心安装；已经安装的电脑直接打开即可，不需要为了本指南重复升级。',
        tag: '已有客户端无需重装',
      },
      {
        title: '输入班级口令',
        text: '输入学校或老师提供的班级口令完成连接。正常情况下无需手工修改服务器地址。',
        tag: '使用原有班级口令',
      },
      {
        title: '确认显示已连接',
        text: '看到“已连接”及当前班级信息后即可最小化运行。同一班级建议只保留一台主接收电脑在线。',
        tag: '保持后台运行',
      },
      {
        title: '接收广播和文件',
        text: '老师发送后客户端会自动接收。请保持电脑联网、扬声器可用，并按提示打开已接收文件。',
        tag: '无需反复登录',
      },
    ],
    features: ['支持存量客户端版本', '自动接收广播', '班级文件实时送达'],
    callout: '客户端已显示“已连接”时，请不要频繁退出登录或修改连接设置。',
  },
  workbench: {
    eyebrow: '教师桌面主工作端',
    title: '教师工作台',
    description: '适合教师在电脑上集中处理广播、班级、学生、考勤、积分和日常教学事务。',
    icon: <LaptopOutlined />,
    color: '#0f6feb',
    steps: [
      {
        title: '下载并安装工作台',
        text: '从官网下载中心获取教师工作台安装包，按提示完成安装；已安装的电脑直接打开即可。',
        tag: '使用官方安装包',
      },
      {
        title: '登录教师身份',
        text: '使用学校分配的教师账号登录，也可按登录页提示使用教师口令或微信扫码登录。',
        tag: '教师统一账号',
      },
      {
        title: '选择学校和班级',
        text: '进入后先确认当前学校、教师身份和已授权班级，再选择需要处理的工作模块。',
        tag: '先核对权限范围',
      },
      {
        title: '完成教学与班级工作',
        text: '可使用已开放的广播、学生、考勤、积分、作业和教师工具；操作结果同步到服务器。',
        tag: '数据多端同步',
      },
    ],
    features: ['教师业务集中处理', '广播与班级管理', '真实账号和权限数据'],
    callout: '页面中标记为暂未开放或被禁用的功能，表示对应服务能力尚未启用，请勿把它视为操作失败。',
  },
  wechat: {
    eyebrow: '老师移动端',
    title: '微信小程序',
    description: '适合老师移动办公，可快速选择班级、发送通知，并随时查看任务进度。',
    icon: <WechatOutlined />,
    color: '#16a34a',
    steps: [
      {
        title: '打开微信小程序',
        text: '在微信中搜索并进入小喇叭广播系统小程序，按页面提示完成微信授权。',
        tag: '微信内使用',
      },
      {
        title: '登录并绑定身份',
        text: '使用学校分配的账号登录；首次使用请按提示完成学校和教师身份绑定。',
        tag: '仅首次绑定',
      },
      {
        title: '选择目标班级',
        text: '发送前核对学校、班级和接收设备状态，避免内容发送到错误班级。',
        tag: '发送前核对',
      },
      {
        title: '发送并查看结果',
        text: '选择文字、音频或文件后发送，并在记录中查看送达状态；异常时可重新发送。',
        tag: '结果可追踪',
      },
    ],
    features: ['随时随地发送', '班级快速切换', '发送记录可查询'],
    callout: '发送前请确认目标班级，涉及多班级时建议逐一核对后再提交。',
  },
  mobileApp: {
    eyebrow: 'iOS 与 Android 移动端',
    title: '手机 APP',
    description: '适合教师和家长在手机或平板上处理班级、学生、广播、学校任务和反馈。',
    icon: <MobileOutlined />,
    color: '#ea6a22',
    steps: [
      {
        title: '查看当前发布状态',
        text: '前往官网下载中心查看 iOS 或 Android 当前可用版本；未提供下载时，请等待官方发布。',
        tag: '认准官方渠道',
      },
      {
        title: '安装并授权必要权限',
        text: '安装后根据需要授权通知、相机、扫码、麦克风或文件访问；拒绝非当前功能所需的权限不会影响普通登录。',
        tag: '权限按需开启',
      },
      {
        title: '登录并选择身份',
        text: '使用统一账号登录，根据实际身份进入教师端或家长端，并核对所属学校、班级或绑定学生。',
        tag: '教师与家长空间',
      },
      {
        title: '处理移动业务',
        text: '从班级、学生、广播、工具和我的五个主入口进入功能；提交后可在其他产品端查看同步结果。',
        tag: '五个移动主入口',
      },
    ],
    features: ['iOS 与 Android 共用业务', '教师和家长角色空间', '移动操作多端同步'],
    callout: '手机 APP 当前处于正式发布准备阶段，请以官网下载中心显示的可用版本和安装说明为准。',
  },
  web: {
    eyebrow: '学校管理端',
    title: '网页管理后台',
    description: '适合学校管理员和老师集中管理班级、设备、内容与发送记录。',
    icon: <GlobalOutlined />,
    color: '#7c3aed',
    steps: [
      {
        title: '登录管理后台',
        text: '使用学校分配的管理账号登录。不要在公共电脑保存密码，使用结束后请退出账号。',
        tag: '保护账号安全',
      },
      {
        title: '进入工作台',
        text: '先检查学校信息、班级数量和在线设备概况，再进入对应功能模块。',
        tag: '先看运行概况',
      },
      {
        title: '维护班级和成员',
        text: '按实际情况新增或导入班级、教师和学生数据，修改前注意核对学校归属。',
        tag: '数据归属要准确',
      },
      {
        title: '广播与数据管理',
        text: '创建广播任务、上传文件并查看发送记录；遇到异常可结合设备在线状态定位。',
        tag: '全流程可查询',
      },
    ],
    features: ['集中管理学校数据', '统一发布广播任务', '设备和记录可追踪'],
    callout: '涉及删除、批量导入或跨班级发送前，请再次核对学校和班级范围。',
  },
};

const faqItems = [
  {
    key: 'connect',
    label: '桌面客户端提示无法连接服务器怎么办？',
    children: <p>先确认电脑能正常上网，再核对班级口令是否完整、班级是否仍在使用。关闭客户端后重新打开并输入原口令即可；不要随意修改服务器地址。仍无法连接时，请记录出现时间、班级名称和页面提示后联系客服。</p>,
  },
  {
    key: 'duplicate',
    label: '提示重复登录或同一班级已有设备在线怎么办？',
    children: <p>同一班级建议只保留一台主要接收电脑。请先退出旧电脑上的客户端，等待约 30 秒后，再在当前电脑重新连接。</p>,
  },
  {
    key: 'sound',
    label: '显示已接收，但教室没有声音怎么办？',
    children: <p>依次检查系统音量、扬声器电源、默认播放设备和客户端音量。可先播放电脑本地音频，确认硬件本身正常。</p>,
  },
  {
    key: 'file',
    label: '文件接收慢或无法打开怎么办？',
    children: <p>请保持客户端在线并等待下载完成，避免重复点击。若文件已下载但无法打开，请确认电脑已安装对应格式的软件，并记录文件名联系发送老师。</p>,
  },
  {
    key: 'wechat',
    label: '微信小程序无法看到学校或班级怎么办？',
    children: <p>先确认登录的是学校分配的教师账号。首次使用需完成身份绑定；若学校或班级仍缺失，请联系学校管理员核对账号权限和班级归属。</p>,
  },
  {
    key: 'workbench',
    label: '教师工作台登录后看不到学校或班级怎么办？',
    children: <p>先确认使用的是学校分配的教师账号，并检查当前选择的学校身份。若账号能登录但班级缺失，请联系学校管理员核对教师与班级的授权关系。</p>,
  },
  {
    key: 'mobile-app',
    label: '手机 APP 在哪里下载？',
    children: <p>请以官网“下载中心”展示的 iOS 或 Android 版本为准。若当前没有提供对应平台下载，表示仍在发布准备中，请勿安装来源不明的安装包。</p>,
  },
  {
    key: 'class',
    label: '输入口令后提示班级不存在或已停用怎么办？',
    children: <p>请向学校管理员重新确认口令，注意区分字母和数字。若口令无误，可能是班级已归档或停用，需要管理员恢复后才能继续使用。</p>,
  },
];

function GuidePanel({ section }) {
  return (
    <div className="guide-panel" style={{ '--guide-accent': section.color }}>
      <div className="guide-panel-heading">
        <div>
          <span className="guide-eyebrow">{section.eyebrow}</span>
          <Title level={2}>{section.title}使用步骤</Title>
          <Paragraph>{section.description}</Paragraph>
        </div>
        <div className="guide-panel-icon">{section.icon}</div>
      </div>

      <div className="guide-feature-grid">
        {section.features.map((feature) => (
          <div className="guide-feature" key={feature}>
            <CheckCircleFilled />
            <span>{feature}</span>
          </div>
        ))}
      </div>

      <div className="guide-step-grid">
        {section.steps.map((step, index) => (
          <article className="guide-step-card" key={step.title}>
            <div className="guide-step-number">{String(index + 1).padStart(2, '0')}</div>
            <div className="guide-step-copy">
              <span className="guide-step-tag">{step.tag}</span>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </div>
          </article>
        ))}
      </div>

      <div className="guide-callout">
        <SafetyCertificateOutlined />
        <span>{section.callout}</span>
      </div>
    </div>
  );
}

export default function GuidePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('desktop');
  const [contactVisible, setContactVisible] = useState(false);

  const tabItems = useMemo(() => Object.entries(guideSections).map(([key, section]) => ({
    key,
    label: <span>{section.icon}{section.title}</span>,
    children: <GuidePanel section={section} />,
  })), []);

  const openGuide = (key = 'desktop') => {
    setActiveTab(key);
    window.requestAnimationFrame(() => {
      document.getElementById('guide-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <Layout className="guide-page">
      <PublicHeader />
      <Content className="guide-page-content">
        <section className="guide-hero">
          <div className="guide-container guide-hero-inner">
            <div className="guide-hero-copy">
              <span className="guide-hero-kicker"><SoundOutlined /> 小喇叭广播系统使用指南</span>
              <h1>从第一次登录，<br />到广播送达教室</h1>
              <p>按照使用场景选择入口。已安装的桌面客户端无需升级，继续使用原有班级口令即可连接和接收内容。</p>
              <div className="guide-hero-actions">
                <Button type="primary" size="large" onClick={() => openGuide('desktop')}>查看桌面端步骤 <ArrowRightOutlined /></Button>
                <Button ghost size="large" icon={<DownloadOutlined />} onClick={() => navigate('/download')}>前往下载中心</Button>
              </div>
              <div className="guide-assurances">
                <span><CheckCircleFilled /> 无需反复登录</span>
                <span><CheckCircleFilled /> 班级口令连接</span>
                <span><CheckCircleFilled /> 广播与文件实时接收</span>
              </div>
            </div>

            <aside className="guide-hero-board">
              <span className="guide-board-label">桌面端 · 3 个关键动作</span>
              <ol>
                <li><b>01</b><span>输入学校提供的班级口令</span></li>
                <li><b>02</b><span>确认客户端显示“已连接”</span></li>
                <li><b>03</b><span>保持后台运行，接收广播和文件</span></li>
              </ol>
              <div className="guide-board-tip"><LockOutlined /> 正常使用不需要手工修改服务器地址</div>
            </aside>
          </div>
        </section>

        <section className="guide-channel-section guide-container">
          <div className="guide-section-heading">
            <span className="guide-eyebrow">选择你的使用入口</span>
            <Title level={2}>五个入口，一套清晰流程</Title>
            <Paragraph>点击对应入口，即可查看完整操作步骤和使用注意事项。</Paragraph>
          </div>
          <div className="guide-channel-grid">
            {Object.entries(guideSections).map(([key, section]) => (
              <button className="guide-channel-card" key={key} type="button" onClick={() => openGuide(key)} style={{ '--guide-accent': section.color }}>
                <span className="guide-channel-icon">{section.icon}</span>
                <span className="guide-channel-copy">
                  <small>{section.eyebrow}</small>
                  <strong>{section.title}</strong>
                  <span>{section.description}</span>
                </span>
                <ArrowRightOutlined className="guide-channel-arrow" />
              </button>
            ))}
          </div>
        </section>

        <section className="guide-detail-section" id="guide-detail">
          <div className="guide-container">
            <Tabs className="guide-tabs" activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
          </div>
        </section>

        <section className="guide-help-section guide-container">
          <div className="guide-section-heading guide-help-heading">
            <span className="guide-eyebrow">遇到问题先看这里</span>
            <Title level={2}>常见问题与快速自查</Title>
          </div>
          <div className="guide-help-grid">
            <div className="guide-faq-card">
              <Collapse ghost accordion items={faqItems} expandIconPosition="end" />
            </div>
            <aside className="guide-check-card">
              <QuestionCircleOutlined className="guide-check-icon" />
              <span className="guide-board-label">连接异常 · 先检查 4 项</span>
              <ol>
                <li><b>1</b><span>电脑是否能正常访问网络</span></li>
                <li><b>2</b><span>班级口令是否完整且未停用</span></li>
                <li><b>3</b><span>同一班级旧电脑是否仍在线</span></li>
                <li><b>4</b><span>退出客户端后重新打开连接</span></li>
              </ol>
              <p>联系客服时请提供：发生时间、学校和班级名称、客户端提示截图。请勿发送账号密码。</p>
            </aside>
          </div>
        </section>

        <section className="guide-support-section">
          <div className="guide-container guide-support-inner">
            <div>
              <span className="guide-eyebrow">仍然没有解决？</span>
              <Title level={2}>把问题交给技术支持</Title>
              <Paragraph>工作时间内联系我们，并提供必要的故障信息，我们会协助定位。</Paragraph>
            </div>
            <div className="guide-support-actions">
              <Button type="primary" size="large" icon={<CustomerServiceOutlined />} onClick={() => setContactVisible(true)}>联系技术支持</Button>
              <a href="tel:17820345350"><MobileOutlined /> 17820345350</a>
            </div>
          </div>
        </section>
      </Content>
      <PublicFooter />

      <Modal className="guide-contact-modal" title="联系技术支持" open={contactVisible} onCancel={() => setContactVisible(false)} footer={null} centered>
        <div className="guide-contact-content">
          <img src="/wechat+.jpg" alt="技术支持微信二维码" />
          <div>
            <Title level={4}>扫码添加技术支持微信</Title>
            <Paragraph>请备注学校和班级名称，便于快速核对。</Paragraph>
            <p><CustomerServiceOutlined /> 电话：<a href="tel:17820345350">17820345350</a></p>
            <p><MessageOutlined /> 邮箱：<a href="mailto:284516383@qq.com">284516383@qq.com</a></p>
            <Text type="secondary">请勿通过聊天发送账号密码。</Text>
          </div>
        </div>
      </Modal>
    </Layout>
  );
}
